/*!
 * Oasis Land — Klaviyo behavioural tracking helper
 * ------------------------------------------------
 * Single source of truth for site-wide Klaviyo events and profile-property
 * writes. Loaded once per page. See docs/klaviyo-tracking-design.md for the
 * event/property catalogue and design rationale.
 *
 * Public API (exposed on window.OasisTrack):
 *   track(name, properties)        Fire a Klaviyo event with deduplication
 *                                  and a session+page context envelope.
 *   identify(properties)            Update the Klaviyo profile.
 *   setProperty(key, value)         Single-key identify().
 *   ready(callback)                 Run after marketing consent + klaviyo.js.
 *   getSessionId()                  Read the current 30-min session UUID.
 *   getFirstTouch()                  Read the sticky first-touch UTM bundle.
 *   isIdentified()                   True once we hold a resolving identifier.
 *
 * Identity gate: Klaviyo auto-creates a profile for any cookie that sends an
 * event — or a profile-property write — without an identifier that resolves
 * to an existing profile. To avoid spawning anonymous profiles, NOTHING is
 * sent until the visitor is identified: a known email (logged-in customer /
 * prior identify() / __kla_id cookie), a _kx email-click token, or an
 * identify() carrying email/$id/phone. An anonymous_id alone does NOT qualify.
 * See isIdentified() and the brief klaviyo-anon-profile-gating.
 *
 * Buffering: when called before identification, before Klaviyo loads, OR
 * before marketing consent, track() and identify() go into an in-memory queue
 * (capped at 50). The queue drains on the FIRST tick after ALL THREE
 * (identified + consent + klaviyo.js) hold — so a visitor's pre-identification
 * browsing replays onto their real profile the moment they identify. If that
 * never happens within 30 minutes (or the page unloads), the queue is dropped
 * silently and no profile is ever created.
 *
 * Storage: localStorage only for cross-session counters / idempotency
 * tokens, never PII. Keys:
 *   oasis_track_session            Current session UUID + last-seen ms.
 *   oasis_track_first_touch        First-touch UTM bundle (utm_*, fbclid).
 *   oasis_track_fired              Dedup set per session (Set encoded as
 *                                  comma-separated keys). Capped at 200.
 *
 * Consent: integrates with window.OasisConsent. No event is sent to
 * Klaviyo unless OasisConsent.allowed('marketing') is true at send time.
 *
 * Page-context input: the liquid snippet `snippets/oasis-events.liquid`
 * sets window.OasisTrackContext before this file boots; we read it once.
 */

(function () {
  'use strict';

  if (window.OasisTrack && window.OasisTrack.__loaded) return;

  // ---------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------
  var SESSION_IDLE_MS = 30 * 60 * 1000;            // 30 minutes
  var listingPage = !!document.querySelector('[data-ll]');
  var BUFFER_MAX = listingPage ? 250 : 50;                              // events held pre-consent
  var BUFFER_DROP_MS = 30 * 60 * 1000;             // 30 minutes
  var DEDUP_SET_MAX = 200;                          // session-scoped fired set
  var SCROLL_THRESHOLDS = [25, 50, 75, 100];
  var VIDEO_THRESHOLDS = [25, 50, 75, 100];
  var ARTICLE_READ_MIN_SECONDS = 30;
  var ARTICLE_READ_MIN_SCROLL = 75;
  var CALCULATOR_DEBOUNCE_MS = 800;
  var STORAGE_KEYS = {
    session: 'oasis_track_session',
    firstTouch: 'oasis_track_first_touch',
    fired: 'oasis_track_fired'
  };

  // ---------------------------------------------------------------------
  // YouTube IFrame API console-noise filter
  // ---------------------------------------------------------------------
  // YouTube's www-widgetapi.js fires postMessage calls back to the parent
  // window with targetOrigin='https://www.youtube.com', but the parent's
  // actual origin is whatever your site is (oasis-land.com). The browser
  // refuses the call and logs an error every ~250ms per embed. The errors
  // are harmless — they're inside YouTube's own code, can't be try/catched
  // from outside the iframe — but a Knowledge Hub page with 19 embeds can
  // pile up hundreds in a minute. We monkey-patch console.error /
  // console.warn to filter that specific message out, leaving every other
  // log intact. Idempotent (won't double-patch if the helper hot-reloads).
  if (!window.__oasisYTFilterInstalled) {
    window.__oasisYTFilterInstalled = true;
    var YT_NOISE_MARKER = "Failed to execute 'postMessage' on 'DOMWindow'";
    function isYTPostMessageNoise(args) {
      for (var i = 0; i < args.length; i++) {
        var a = args[i];
        var s = (typeof a === 'string') ? a : (a && a.message) ? a.message : '';
        if (typeof s === 'string' && s.indexOf(YT_NOISE_MARKER) > -1) return true;
      }
      return false;
    }
    ['error', 'warn'].forEach(function (level) {
      var original = console[level];
      if (typeof original !== 'function') return;
      console[level] = function () {
        if (isYTPostMessageNoise(arguments)) return; // swallow
        return original.apply(console, arguments);
      };
    });
    // Belt-and-braces: also suppress uncaught-error surfacing for the same
    // text. Some Chrome versions route postMessage origin failures through
    // window 'error' events rather than console.error.
    window.addEventListener('error', function (e) {
      if (e && e.message && e.message.indexOf(YT_NOISE_MARKER) > -1) {
        e.preventDefault();
        return true;
      }
    }, true);
  }

  // ---------------------------------------------------------------------
  // Tiny utilities (no jQuery, no polyfills beyond evergreen browsers)
  // ---------------------------------------------------------------------
  var ctx = window.OasisTrackContext || {};
  var debug = !!(window.OasisDebug || (typeof URLSearchParams !== 'undefined' && new URLSearchParams(location.search).has('oasis_debug')));
  function log() {
    if (!debug) return;
    try { console.log.apply(console, ['[OasisTrack]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  function readLocal(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }
  function writeLocal(key, value) {
    try { localStorage.setItem(key, value); } catch (e) {}
  }

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'oxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function now() { return Date.now(); }
  function nowIso() { return new Date().toISOString(); }

  function throttle(fn, wait) {
    var last = 0;
    var raf = 0;
    return function () {
      var args = arguments;
      var t = now();
      if (t - last >= wait) {
        last = t;
        fn.apply(null, args);
      } else if (!raf) {
        raf = requestAnimationFrame(function () {
          raf = 0;
          last = now();
          fn.apply(null, args);
        });
      }
    };
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  function isObject(v) { return v && typeof v === 'object' && !Array.isArray(v); }

  // Format hyphenated / uppercase view-type codes (e.g. "garden-view",
  // "OCEAN_VIEW") into the human-readable Title Case strings the
  // Welcome-flow emails compare against (e.g. "Garden View", "Oceanfront").
  // Returns '' for nullish/empty input so callers can skip the property.
  // Per DEV_BRIEF_last_lot_viewed.md, the email contains
  //   {% if person|lookup:'Last Lot Viewed View Type' == 'Oceanfront' %}
  // which only matches an exact, capitalised string.
  function formatViewType(raw) {
    if (!raw) return '';
    return String(raw)
      .toLowerCase()
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); })
      .join(' ');
  }

  // ---------------------------------------------------------------------
  // Session management — 30-minute idle window, UUID kept in localStorage
  // ---------------------------------------------------------------------
  function readSession() {
    var raw = readLocal(STORAGE_KEYS.session);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.id || !parsed.lastSeen) return null;
      if (now() - parsed.lastSeen > SESSION_IDLE_MS) return null;
      return parsed;
    } catch (e) { return null; }
  }
  function writeSession(sess) {
    writeLocal(STORAGE_KEYS.session, JSON.stringify({ id: sess.id, lastSeen: now() }));
  }
  function getSession() {
    var s = readSession();
    if (!s) {
      s = { id: uuid(), lastSeen: now() };
      writeLocal(STORAGE_KEYS.session, JSON.stringify(s));
    } else {
      writeSession(s);
    }
    return s;
  }
  var session = getSession();
  // Refresh the last-seen on every interaction so the window slides.
  ['pointerdown', 'keydown', 'scroll', 'visibilitychange'].forEach(function (evt) {
    window.addEventListener(evt, function () { writeSession(session); }, { passive: true });
  });

  // ---------------------------------------------------------------------
  // First-touch UTM capture — sticky for the life of the browser profile.
  // ---------------------------------------------------------------------
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid'];
  function captureFirstTouch() {
    if (readLocal(STORAGE_KEYS.firstTouch)) return;
    try {
      var sp = new URLSearchParams(location.search);
      var bundle = {};
      var seen = false;
      UTM_KEYS.forEach(function (k) {
        var v = sp.get(k);
        if (v) { bundle[k] = v; seen = true; }
      });
      if (!seen) return;
      bundle.captured_at = nowIso();
      bundle.captured_path = location.pathname;
      writeLocal(STORAGE_KEYS.firstTouch, JSON.stringify(bundle));
    } catch (e) {}
  }
  captureFirstTouch();

  function getFirstTouch() {
    var raw = readLocal(STORAGE_KEYS.firstTouch);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  // ---------------------------------------------------------------------
  // Per-session dedup set
  // ---------------------------------------------------------------------
  function readFiredSet() {
    var raw = sessionStorage.getItem(STORAGE_KEYS.fired);
    if (!raw) return {};
    try { return JSON.parse(raw) || {}; } catch (e) { return {}; }
  }
  function writeFiredSet(set) {
    try { sessionStorage.setItem(STORAGE_KEYS.fired, JSON.stringify(set)); } catch (e) {}
  }
  function markFired(key) {
    var set = readFiredSet();
    var keys = Object.keys(set);
    if (keys.length >= DEDUP_SET_MAX) {
      // FIFO eviction
      delete set[keys[0]];
    }
    set[key] = 1;
    writeFiredSet(set);
  }
  function hasFired(key) {
    return !!readFiredSet()[key];
  }

  // ---------------------------------------------------------------------
  // Consent + Klaviyo readiness
  // ---------------------------------------------------------------------
  function marketingAllowed() {
    return !!(window.OasisConsent && window.OasisConsent.allowed && window.OasisConsent.allowed('marketing'));
  }

  function klaviyoReady() {
    // Onsite JS exposes the global `klaviyo` (and the legacy `_learnq` queue).
    return !!(window._learnq || window.klaviyo);
  }

  // ---------------------------------------------------------------------
  // Suppress Klaviyo's auto-fired "Viewed Product".
  // The Klaviyo onsite JS auto-detects Shopify product pages and pushes
  // `['track', 'Viewed Product', {...plain payload...}]` to _learnq. We
  // fire our own enriched `Viewed Lot` event instead, so the default
  // Viewed Product is redundant noise. We monkey-patch _learnq.push to
  // intercept and drop those default Viewed Product entries. Our own
  // events (which use the metric names "Viewed Lot", "Quiz Step
  // Completed", etc.) pass through untouched.
  // ---------------------------------------------------------------------
  function isDefaultViewedProductCall(args) {
    if (!args) return false;
    var arr = args;
    if (arguments.length > 1) arr = Array.prototype.slice.call(arguments);
    if (!Array.isArray(arr) || arr.length < 2) return false;
    if (arr[0] !== 'track') return false;
    if (arr[1] !== 'Viewed Product') return false;
    // Klaviyo's default payload uses keys like ProductID / Name / Brand
    // (PascalCase). Our helper never fires Viewed Product. So any push
    // with name === 'Viewed Product' that we see is from Klaviyo's
    // onsite JS and should be dropped.
    return true;
  }

  function installLearnqFilter() {
    if (!window._learnq) window._learnq = [];
    var q = window._learnq;
    if (q.__oasisFiltered) return;
    q.__oasisFiltered = true;
    var origPush = q.push;
    q.push = function () {
      // Klaviyo calls push with a single array arg most of the time.
      var first = arguments[0];
      if (isDefaultViewedProductCall(first)) {
        log('suppressed default Viewed Product');
        return q.length; // pretend it pushed, return new length
      }
      // Also handle the multi-arg form just in case.
      if (arguments.length > 1) {
        var collected = Array.prototype.slice.call(arguments);
        if (isDefaultViewedProductCall(collected)) {
          log('suppressed default Viewed Product (multi-arg form)');
          return q.length;
        }
      }
      return origPush.apply(q, arguments);
    };
  }

  // Install at boot, and re-install on a timer for ~15s in case
  // klaviyo.js replaces the _learnq object after it loads.
  installLearnqFilter();
  var filterAttempts = 0;
  var filterPoll = setInterval(function () {
    filterAttempts++;
    installLearnqFilter();
    if (filterAttempts > 30) clearInterval(filterPoll);
  }, 500);

  // ---------------------------------------------------------------------
  // Buffering + drain
  // ---------------------------------------------------------------------
  var buffer = [];
  var bufferStartedAt = now();
  function pushBuffer(entry) {
    if (now() - bufferStartedAt > BUFFER_DROP_MS) {
      buffer.length = 0;
      bufferStartedAt = now();
    }
    if (buffer.length >= BUFFER_MAX) buffer.shift();
    buffer.push(entry);
  }

  function drainOnce() {
    // Never flush the buffer for a visitor we can't resolve to a real profile
    // — replaying held events without an identity would create the very
    // anonymous profiles this gate exists to prevent.
    if (!marketingAllowed() || !klaviyoReady() || !isIdentified()) return;
    if (!buffer.length) return;
    log('Draining buffer with', buffer.length, 'entries');
    var pending = buffer.splice(0);
    pending.forEach(function (entry) {
      if (entry.type === 'track') {
        _sendTrack(entry.name, entry.props);
        // Also beacon: covers the case where this event was buffered before
        // marketing consent (no beacon fired in api.track) and only now
        // becomes eligible. Klaviyo dedupes against $event_id if it's
        // already in via _learnq.
        try { sendEventBeacon(entry.name, entry.props); } catch (err) {}
      } else if (entry.type === 'identify') {
        _sendIdentify(entry.props);
        try { sendProfileBeacon(entry.props); } catch (err) {}
      }
    });
  }

  // ---------------------------------------------------------------------
  // Direct send (private)
  // ---------------------------------------------------------------------
  function _sendTrack(name, props) {
    try {
      window._learnq = window._learnq || [];
      var enriched = enrichProperties(props || {});
      if (listingPage && window.klaviyo && typeof window.klaviyo.track === 'function') {
        window.klaviyo.track(name, enriched, function (accepted) {
          var diag = document.getElementById('ll-tracking-status');
          if (diag) { diag.setAttribute('data-last-sdk-result', String(accepted)); diag.setAttribute('data-last-sdk-event', name); }
        });
        return;
      }
      window._learnq.push(['track', name, enriched]);
      log('track →', name, enriched);
    } catch (e) { log('track failed', name, e); }
  }
  function _sendIdentify(props) {
    try {
      if (!isObject(props)) return;
      if (listingPage && window.klaviyo && typeof window.klaviyo.identify === 'function') {
        window.klaviyo.identify(props);
        return;
      }
      window._learnq = window._learnq || [];
      window._learnq.push(['identify', props]);
      log('identify →', props);
    } catch (e) { log('identify failed', e); }
  }

  function enrichProperties(props) {
    var enriched = {};
    var k;
    for (k in props) if (props.hasOwnProperty(k)) enriched[k] = props[k];

    enriched.session_id = session.id;
    enriched.page_type = ctx.pageType || (document.body && document.body.dataset ? document.body.dataset.pageType : '');
    enriched.path = location.pathname;
    // 27 Aug 2026: every event already carried the path, but nothing carried
    // a human-readable page name, so a card could only ever say "visited a
    // page". Stamp the title on everything, without clobbering an event that
    // set its own.
    if (!enriched.title) enriched.title = (document.title || '').slice(0, 200);
    if (!enriched.page_title) enriched.page_title = enriched.title;
    if (!enriched.lot_handle && ctx.product && ctx.product.handle) {
      enriched.lot_handle = ctx.product.handle;
    }
    enriched.locale = ctx.locale || (document.documentElement && document.documentElement.lang) || '';
    enriched.market_handle = ctx.marketHandle || '';
    enriched.event_source_url = location.href;

    var ft = getFirstTouch();
    if (ft) {
      if (ft.utm_source) enriched.first_touch_utm_source = ft.utm_source;
      if (ft.utm_medium) enriched.first_touch_utm_medium = ft.utm_medium;
      if (ft.utm_campaign) enriched.first_touch_utm_campaign = ft.utm_campaign;
    }
    return enriched;
  }

  // ---------------------------------------------------------------------
  // Direct-to-Klaviyo beacon (parallel to _learnq)
  // ---------------------------------------------------------------------
  // iOS Safari's tracking prevention can fully neuter klaviyo.js: the
  // script loads but its outbound requests are blocked or its cookies
  // never persist, so events pushed to _learnq silently die. Mitigation:
  // also POST each event directly to Klaviyo's public Client Events API
  // via fetch+keepalive, which survives page unload and doesn't rely on
  // klaviyo.com cookies. Klaviyo dedupes by $event_id / unique_id, so
  // running both paths on browsers where klaviyo.js works is a no-op.
  //
  // Profile identifiers attached to every beacon event (any/all of):
  //   email        — ctx.customer / localStorage / __kla_id cookie
  //   _kx          — Klaviyo's encrypted email-click token (URL > localStorage).
  //                  Klaviyo decrypts server-side; resolves to the real
  //                  profile even when klaviyo.js never ran.
  //   anonymous_id — UUID we generate on first visit and persist. Klaviyo
  //                  retroactively merges this anonymous profile with the
  //                  real one when we later identify (via email/$id/_kx).
  // At least one is always set (anonymous_id is guaranteed), so beacons
  // are never dropped for lack of identity.
  // ---------------------------------------------------------------------
  var KLAVIYO_COMPANY_ID = 'UNnEsQ';
  var KLAVIYO_REVISION = '2024-10-15';
  var KLAVIYO_EVENTS_URL = 'https://a.klaviyo.com/client/events/?company_id=' + KLAVIYO_COMPANY_ID;
  var KLAVIYO_PROFILES_URL = 'https://a.klaviyo.com/client/profiles/?company_id=' + KLAVIYO_COMPANY_ID;
  var KNOWN_EMAIL_KEY = 'oasis_known_email';
  var KX_KEY = 'oasis_known_kx';
  var ANON_ID_KEY = 'oasis_anonymous_id';

  // Capture _kx from the landing URL into localStorage immediately —
  // in-site navigations drop the URL parameter, but Klaviyo can still
  // resolve the encrypted token from any subsequent page.
  (function captureKxFromUrl() {
    try {
      var urlKx = new URLSearchParams(location.search).get('_kx');
      if (urlKx) localStorage.setItem(KX_KEY, urlKx);
    } catch (e) {}
  })();

  function rememberEmail(email) {
    if (!email || typeof email !== 'string' || !/@/.test(email)) return;
    try { localStorage.setItem(KNOWN_EMAIL_KEY, email); } catch (e) {}
  }

  function getKnownEmail() {
    if (ctx.customer && ctx.customer.email && /@/.test(ctx.customer.email)) return ctx.customer.email;
    try {
      var saved = localStorage.getItem(KNOWN_EMAIL_KEY);
      if (saved && /@/.test(saved)) return saved;
    } catch (e) {}
    // __kla_id is base64url(JSON({$email, $exchange_id, ...})) on success
    try {
      var m = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
      if (m) {
        var raw = decodeURIComponent(m[1]);
        var json = null;
        try { json = JSON.parse(atob(raw.replace(/-/g, '+').replace(/_/g, '/'))); } catch (e1) {}
        if (json && json.$email && /@/.test(json.$email)) {
          rememberEmail(json.$email);
          return json.$email;
        }
      }
    } catch (e) {}
    return null;
  }

  function getKnownKx() {
    try { return localStorage.getItem(KX_KEY) || null; } catch (e) { return null; }
  }

  function getOrCreateAnonId() {
    try {
      var stored = localStorage.getItem(ANON_ID_KEY);
      if (stored) return stored;
    } catch (e) {}
    var id = uuid();
    try { localStorage.setItem(ANON_ID_KEY, id); } catch (e) {}
    return id;
  }

  // Returns the bag of identifiers to put under profile.data.attributes.
  // Klaviyo accepts email, _kx, anonymous_id, external_id, phone_number
  // simultaneously and uses identity resolution to map to a single profile.
  function getProfileAttrs() {
    var out = {};
    var email = getKnownEmail();
    if (email) out.email = email;
    var kx = getKnownKx();
    if (kx) out._kx = kx;
    out.anonymous_id = getOrCreateAnonId();
    return out;
  }

  // ---------------------------------------------------------------------
  // Identity gate — the heart of the anonymous-profile fix
  // ---------------------------------------------------------------------
  // Klaviyo auto-creates a profile for any browser cookie that sends an
  // event OR a profile-property write without an identifier that resolves to
  // an existing profile. To stop spawning anonymous profiles for visitors who
  // never tell us who they are, NOTHING is sent to Klaviyo until we hold such
  // an identifier:
  //   - a known email  — logged-in customer (ctx.customer), a prior identify()
  //                       on this device, or the __kla_id cookie; or
  //   - a _kx token    — Klaviyo's email-click identifier, which Klaviyo
  //                       resolves server-side to the real profile; or
  //   - an identify()  — call in this page load carrying email / $id / phone.
  // An anonymous_id ALONE does not qualify — minting one for every cookie is
  // exactly what was creating the unwanted profiles. Until the visitor is
  // identified, track() and property-only identify() calls are buffered (not
  // sent) and replayed the instant they identify. See the dev brief:
  // klaviyo-anon-profile-gating.
  var sessionIdentified = false;
  function hasRealIdentifier(props) {
    return !!(props && (props.$email || props.email || props.$id || props.$phone_number));
  }
  function isIdentified() {
    if (sessionIdentified) return true;
    if (getKnownEmail() || getKnownKx()) sessionIdentified = true;
    return sessionIdentified;
  }

  function postBeacon(url, payload) {
    if (typeof fetch !== 'function') return;
    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
        body: JSON.stringify(payload),
        keepalive: true,
        mode: 'cors',
        credentials: 'omit'
      }).catch(function () { /* network blocked / klaviyo down — accept loss */ });
    } catch (e) { /* fetch threw synchronously (rare) */ }
  }

  function sendEventBeacon(name, props) {
    if (listingPage) return;
    var profileAttrs = getProfileAttrs();
    var enriched = enrichProperties(props || {});
    var attrs = {
      properties: enriched,
      metric: { data: { type: 'metric', attributes: { name: name } } },
      profile: { data: { type: 'profile', attributes: profileAttrs } },
      time: nowIso()
    };
    // unique_id can come from the original __unique_id (live api.track call)
    // or from the $event_id we stamped onto buffered events at api.track time.
    var uniqueId = null;
    if (props && props.__unique_id) uniqueId = name + '|' + props.__unique_id;
    else if (props && props.$event_id) uniqueId = props.$event_id;
    if (uniqueId) attrs.unique_id = uniqueId;
    postBeacon(KLAVIYO_EVENTS_URL, { data: { type: 'event', attributes: attrs } });
    log('beacon event →', name, profileAttrs);
  }

  function sendProfileBeacon(props) {
    if (listingPage) return;
    var profileAttrs = getProfileAttrs();
    // Any explicit identifier in the identify() call wins over cached/default
    var providedEmail = props && (props.$email || props.email);
    if (providedEmail) {
      rememberEmail(providedEmail);
      profileAttrs.email = providedEmail;
    }
    profileAttrs.properties = {};
    var key;
    for (key in props) {
      if (!props.hasOwnProperty(key)) continue;
      var v = props[key];
      if (v === undefined || v === null) continue;
      if (key === '$email' || key === 'email') continue;
      if (key === '$first_name') profileAttrs.first_name = String(v);
      else if (key === '$last_name') profileAttrs.last_name = String(v);
      else if (key === '$id') profileAttrs.external_id = String(v);
      else if (key === '$phone_number') profileAttrs.phone_number = String(v);
      else profileAttrs.properties[key] = v;
    }
    postBeacon(KLAVIYO_PROFILES_URL, { data: { type: 'profile', attributes: profileAttrs } });
    log('beacon profile →', profileAttrs);
  }

  // ---------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------
  var readyCallbacks = [];
  function fireReady() {
    while (readyCallbacks.length) {
      try { readyCallbacks.shift()(); } catch (e) { log('ready cb failed', e); }
    }
  }

  var api = {
    __loaded: true,
    track: function (name, props) {
      if (!name || typeof name !== 'string') return;
      if (listingPage && (!marketingAllowed() || navigator.globalPrivacyControl === true)) return;
      var dedupKey = props && props.__unique_id ? name + '|' + props.__unique_id : null;
      var clean = props ? Object.assign({}, props) : {};
      delete clean.__unique_id;
      if (dedupKey && hasFired(dedupKey)) {
        log('skip (deduped)', name, dedupKey);
        return;
      }
      if (dedupKey) {
        markFired(dedupKey);
        // Same key on both paths so Klaviyo dedupes across them ($event_id
        // for the _learnq path maps to unique_id on the REST API).
        clean.$event_id = dedupKey;
      }
      // IDENTITY GATE: emit nothing for a visitor we can't resolve to a real
      // profile — sending here is what spawned anonymous profiles. Hold the
      // event in the buffer; drainOnce() replays it the instant the visitor
      // identifies (form submit, login, _kx click-through).
      if (!isIdentified()) {
        pushBuffer({ type: 'track', name: name, props: clean });
        log('gated — unidentified, buffered', name);
        return;
      }
      // Direct-to-API beacon (survives ITP / unload). Receives original
      // props (with __unique_id intact) so it can build the unique_id.
      if (marketingAllowed()) {
        try { sendEventBeacon(name, props); } catch (e) { log('beacon track failed', e); }
      }
      if (marketingAllowed() && klaviyoReady()) {
        _sendTrack(name, clean);
      } else {
        pushBuffer({ type: 'track', name: name, props: clean });
        log('buffered', name);
      }
    },
    identify: function (props) {
      if (!isObject(props)) return;
      if (listingPage && (!marketingAllowed() || navigator.globalPrivacyControl === true)) return;
      // Cache the email so subsequent track() calls can attach via beacon
      // even if klaviyo.js never loads on this device.
      var em = props.$email || props.email;
      if (em) rememberEmail(em);

      // Does THIS call actually identify the visitor (carry an email / $id /
      // phone)? A properties-only identify — e.g. {'Quiz Vision': '...'} — does
      // NOT, and writing those properties for an unknown cookie is precisely
      // what created the anonymous quiz profiles. The two cases diverge here.
      var identifying = hasRealIdentifier(props);
      if (identifying) sessionIdentified = true;

      // IDENTITY GATE: a properties-only write for a visitor we still can't
      // resolve is held back and replayed once they identify.
      if (!identifying && !isIdentified()) {
        pushBuffer({ type: 'identify', props: props });
        log('gated — unidentified property write, buffered', props);
        return;
      }

      if (marketingAllowed()) {
        // The profile beacon includes both the new email AND our persisted
        // anonymous_id, which lets Klaviyo deterministically merge prior
        // anonymous events into the now-identified profile.
        try { sendProfileBeacon(props); } catch (e) { log('beacon identify failed', e); }
      }
      if (marketingAllowed() && klaviyoReady()) {
        _sendIdentify(props);
      } else {
        pushBuffer({ type: 'identify', props: props });
      }

      // If this call is what identified the visitor, flush everything we held
      // back so their pre-identification activity attaches to the real profile
      // (the identify above is pushed first, so Klaviyo binds the replay to it).
      if (identifying) {
        try { drainOnce(); } catch (e) { log('drain after identify failed', e); }
      }
    },
    setProperty: function (key, value) {
      if (!key) return;
      var p = {}; p[key] = value;
      api.identify(p);
    },
    ready: function (cb) {
      if (typeof cb !== 'function') return;
      if (marketingAllowed() && klaviyoReady()) {
        try { cb(); } catch (e) { log('ready cb failed', e); }
      } else {
        readyCallbacks.push(cb);
      }
    },
    getSessionId: function () { return session.id; },
    getFirstTouch: getFirstTouch,
    isIdentified: isIdentified
  };
  window.OasisTrack = api;

  // ---------------------------------------------------------------------
  // Watch for consent + klaviyo readiness, drain when both happen
  // ---------------------------------------------------------------------
  function tryReady() {
    if (listingPage && (!marketingAllowed() || navigator.globalPrivacyControl === true)) { buffer.length = 0; return false; }
    if (marketingAllowed() && klaviyoReady()) {
      drainOnce();
      fireReady();
      return true;
    }
    return false;
  }

  window.addEventListener('oasis:consent-granted', tryReady);
  window.addEventListener('oasis:consent-updated', tryReady);

  // Poll briefly for klaviyo.js (it loads async via the marketing-consent
  // loader). We back off the poll quickly to avoid background CPU.
  (function pollKlaviyo() {
    var attempts = 0;
    var poll = function () {
      attempts++;
      if (tryReady()) return;
      if (attempts < 60) setTimeout(poll, 250); // up to ~15s
    };
    setTimeout(poll, 250);
  })();

  // ---------------------------------------------------------------------
  // Customer identify — if Shopify customer is logged in, ctx.customer is
  // present. Fire identify with email so subsequent events attach.
  // ---------------------------------------------------------------------
  if (ctx.customer && ctx.customer.email) {
    api.ready(function () {
      api.identify({
        $email: ctx.customer.email,
        $first_name: ctx.customer.first_name || undefined,
        $last_name: ctx.customer.last_name || undefined,
        $id: ctx.customer.id || undefined
      });
    });
  }

  // ---------------------------------------------------------------------
  // EVENT WIRES — each in its own function so it can be added/removed
  // ---------------------------------------------------------------------

  // ----- Viewed Lot (product pages) -----
  function wireViewedLot() {
    if (ctx.pageType !== 'product' || !ctx.product || !ctx.product.handle) return;
    var p = ctx.product;
    // 500ms is short enough that mobile tap-and-bounce users still get caught
    // before navigating away (previous 3s timeout missed nearly all mobile
    // product views). Consent buffering already handles the pre-consent case.
    var fire = function () {
      api.track('Viewed Lot', {
        __unique_id: p.handle + ':' + session.id,
        lot_handle: p.handle,
        lot_id: p.id,
        lot_name: p.title,
        lot_url: p.url,
        lot_price: p.price,
        lot_size: p.metafields && p.metafields.lot_size,
        lot_location: (p.metafields && p.metafields.lot_location) || (p.metafields && p.metafields.location),
        lot_tier: p.metafields && p.metafields.lot_tier,
        development: p.metafields && p.metafields.development,
        island: p.metafields && p.metafields.island,
        view_type: p.metafields && p.metafields.view_type
      });

      // Sticky profile properties for personalisation tokens in the
      // Welcome-flow emails (D.3 "The Numbers" and the equivalent pillar
      // emails). Per DEV_BRIEF_last_lot_viewed.md the 5 properties the
      // emails read via {% if person|lookup:'Last Lot Viewed *' %} are:
      //   - Last Lot Viewed Name        product.title
      //   - Last Lot Viewed Island      custom.location  (NOT custom.islands)
      //   - Last Lot Viewed URL         canonical product URL
      //   - Last Lot Viewed Price       formatted string from custom.lot_price
      //                                 (e.g. "$295,000", NOT raw 295000)
      //   - Last Lot Viewed View Type   capitalised string (e.g. "Oceanfront",
      //                                 NOT "garden-view")
      // Each property is only emitted when its source is non-blank, so a lot
      // with no view_type metafield simply omits that key and the email
      // falls back to its generic copy.
      var mf = p.metafields || {};
      var lastLot = {
        'Last Lot Viewed At': nowIso()
      };
      if (p.handle) lastLot['Last Lot Viewed Handle'] = p.handle;
      if (p.url) lastLot['Last Lot Viewed URL'] = p.url;
      if (p.title) lastLot['Last Lot Viewed Name'] = p.title;
      // Price: prefer the formatted display string from custom.lot_price.
      // Skip the property entirely if the metafield is unset rather than
      // sending a raw number that would render literally in the email.
      if (p.price_display) lastLot['Last Lot Viewed Price'] = p.price_display;
      // Island: read from custom.location per spec.
      if (mf.location) lastLot['Last Lot Viewed Island'] = mf.location;
      // View Type: map the raw metafield code to the email's expected
      // capitalised wording (Garden View / Ocean View / Lakeside / Oceanfront).
      var formattedViewType = formatViewType(mf.view_type);
      if (formattedViewType) lastLot['Last Lot Viewed View Type'] = formattedViewType;
      // Extra context (not used by the personalisation conditionals but
      // useful for segmentation): tier, development, full location string.
      if (mf.lot_tier) lastLot['Last Lot Viewed Tier'] = mf.lot_tier;
      if (mf.development) lastLot['Last Lot Viewed Development'] = mf.development;
      var fullLocation = mf.lot_location || mf.location;
      if (fullLocation) lastLot['Last Lot Viewed Location'] = fullLocation;
      api.identify(lastLot);
    };

    setTimeout(fire, 500);
  }

  // ----- Viewed Lot Drawer (desktop "View Plot Details" button) -----
  function wireLotDrawer() {
    document.addEventListener('click', function (e) {
      var trigger = e.target && e.target.closest && e.target.closest('[data-oasis-view-plot]');
      if (!trigger) return;
      var handle = trigger.getAttribute('data-product-handle') || trigger.getAttribute('data-handle') || '';
      var id = trigger.getAttribute('data-product-id') || '';
      var bucket = Math.floor(now() / 60000);
      api.track('Viewed Lot Drawer', {
        __unique_id: (handle || id) + ':' + bucket,
        lot_handle: handle || undefined,
        lot_id: id || undefined,
        lot_url: trigger.getAttribute('data-product-url') || trigger.getAttribute('data-url') || ''
      });
    }, { capture: true, passive: true });
  }

  // ----- Quiz events (subscribe to existing oasis_land:quiz_event) -----
  // The quiz emits exactly three event names we care about:
  //   - 'quiz_start'   : fires once when the welcome-screen CTA is clicked
  //   - 'quiz_step'    : fires per step selection. Payload includes
  //                      step_number (1-4), step_name (goal|island|budget|view),
  //                      and selected_option (the human label).
  //   - 'quiz_complete': fires when results render. Payload includes
  //                      goal, island, budget, view.
  // We ignore quiz_lead_capture and quiz_cta_click here (handled elsewhere
  // or out of scope for scoring).
  function wireQuiz() {
    window.addEventListener('oasis_land:quiz_event', function (e) {
      var d = (e && e.detail) || {};
      var name = d.event || d.quiz_event || '';
      if (!name) return;

      if (name === 'quiz_start') {
        api.track('Quiz Started', {
          __unique_id: session.id + ':quiz-started'
        });
        return;
      }

      if (name === 'quiz_step') {
        var stepNumber = d.step_number;
        var stepName = d.step_name || '';
        var selected = d.selected_option || '';

        api.track('Quiz Step Completed', {
          __unique_id: session.id + ':quiz-step-' + stepNumber,
          step_number: stepNumber,
          step_name: stepName,
          selected_option: selected
        });

        // Sticky profile properties matching the dual-flow interest_tag
        // and island_preference fields specified in the handover.
        var ident = {};
        if (stepName === 'goal' && selected) ident['Quiz Vision'] = selected;
        else if (stepName === 'island' && selected) ident['Quiz Island Preference'] = selected;
        else if (stepName === 'budget' && selected) ident['Quiz Budget Range'] = selected;
        else if (stepName === 'view' && selected) ident['Quiz View Preference'] = selected;
        if (Object.keys(ident).length) api.identify(ident);
        return;
      }

      if (name === 'quiz_complete') {
        api.track('Quiz Completed', {
          __unique_id: session.id + ':quiz-completed',
          goal: d.goal,
          island: d.island,
          budget: d.budget,
          view: d.view,
          matched_lot_handle: d.lot_handle || d.matched_lot_handle || d.matched_lot
        });
        api.identify({
          'Quiz Completed At': nowIso(),
          'Quiz Vision': d.goal || undefined,
          'Quiz Island Preference': d.island || undefined,
          'Quiz Budget Range': d.budget || undefined,
          'Quiz View Preference': d.view || undefined,
          'Quiz Match Lot Handle': d.lot_handle || d.matched_lot_handle || d.matched_lot || undefined
        });
        return;
      }

      // Any other quiz_* event is ignored by this scoring helper.
    });
  }

  // ----- Calculated Payment (budget calculator) -----
  // Two calculator shapes exist on the site:
  //   1) Standalone budget calc (budget-calculator.liquid): uses element
  //      IDs #landPrice / #depositPercentage / #years / #monthlyPaymentResult.
  //   2) Product-page modal calc (product-oasisdrawer-main.liquid): uses
  //      [data-oasis-monthly-display] for the visible amount and
  //      [data-oasis-property="monthly-payment|deposit|term-months|lot-price"]
  //      hidden form inputs that the modal script populates.
  // We read whichever exists and fire Calculated Payment when the value
  // changes via either DOM input events or a MutationObserver on the
  // displayed monthly amount.
  function wireCalculator() {
    var CALC_INPUT_IDS = { landPrice: 1, depositPercentage: 1, years: 1 };

    function readById(id) {
      var el = document.getElementById(id);
      return readVal(el);
    }
    function readVal(el) {
      if (!el) return NaN;
      var raw = el.value !== undefined && el.value !== '' ? el.value : (el.textContent || '');
      var n = parseFloat(String(raw).replace(/[^0-9.\-]/g, ''));
      return isFinite(n) ? n : NaN;
    }
    function readDataProp(name) {
      var el = document.querySelector('[data-oasis-property="' + name + '"]');
      return readVal(el);
    }

    var fire = debounce(function () {
      // Try standalone IDs first, then fall back to data-oasis-property.
      var landPrice = readById('landPrice');
      if (!isFinite(landPrice) || landPrice <= 0) landPrice = readDataProp('lot-price');
      var deposit = readById('depositPercentage');
      if (!isFinite(deposit)) deposit = readDataProp('deposit');
      var years = readById('years');
      if (!isFinite(years) || years <= 0) {
        var termMonths = readDataProp('term-months');
        if (isFinite(termMonths) && termMonths > 0) years = Math.round(termMonths / 12);
      }
      // Monthly: try #monthlyPaymentResult, then the hidden form input,
      // then the visible display element.
      var monthly = readById('monthlyPaymentResult');
      if (!isFinite(monthly) || monthly <= 0) monthly = readDataProp('monthly-payment');
      if (!isFinite(monthly) || monthly <= 0) {
        var disp = document.querySelector('[data-oasis-monthly-display]');
        monthly = readVal(disp);
      }
      if (!isFinite(monthly) || monthly <= 0) return;
      if (!isFinite(landPrice) || landPrice <= 0) {
        // Fall back to the product context if we're on a product page.
        landPrice = (ctx.product && ctx.product.price) || NaN;
      }
      if (!isFinite(landPrice) || landPrice <= 0) return;

      var handle = (ctx.product && ctx.product.handle) || '';
      api.track('Calculated Payment', {
        __unique_id: session.id + ':' + handle + ':' + Math.round(monthly),
        lot_handle: handle || undefined,
        land_price: landPrice,
        deposit_pct: deposit,
        years: years,
        monthly_payment: Math.round(monthly * 100) / 100
      });

      api.identify({
        'Last Calculator Monthly Payment': Math.round(monthly * 100) / 100,
        'Last Calculator Deposit Pct': deposit,
        'Last Calculator Years': years,
        'Last Calculator Land Price': landPrice,
        'Last Calculator At': nowIso()
      });
    }, CALCULATOR_DEBOUNCE_MS);

    function maybeFireFromTarget(target) {
      if (!target) return;
      if (target.id && CALC_INPUT_IDS[target.id]) { realInteractionTs = now(); fire(); return; }
      // Modal calc inputs: anything declaring its role with data-oasis-property
      // (term-months, deposit, monthly-payment, lot-price, payment-mode).
      if (target.getAttribute && target.getAttribute('data-oasis-property')) { realInteractionTs = now(); fire(); return; }
      // Also any input inside the payment calc modal.
      if (target.closest && target.closest('#paymentCalcModal, .modal-reservation-form, .calc-modal-content')) {
        realInteractionTs = now();
        fire();
      }
    }

    // Delegated listeners catch any calc, regardless of when it mounts.
    document.addEventListener('input', function (e) { maybeFireFromTarget(e.target); }, true);
    document.addEventListener('change', function (e) { maybeFireFromTarget(e.target); }, true);
    document.addEventListener('click', function (e) {
      // Many modal calcs use clickable option chips (e.g. deposit %, term)
      // that mutate the form values via JS without firing a native input
      // event. Catch any click inside the modal calc area.
      var modalEl = e.target && e.target.closest && e.target.closest('#paymentCalcModal, .calc-modal-content');
      if (modalEl) { realInteractionTs = now(); fire(); }
    }, true);

    // Watch ALL "monthly payment" display elements for textContent changes
    // — the existing calculator scripts mutate them directly, so this
    // catches calculations done by chips / sliders that don't fire input.
    var observer;
    // Real-interaction guard (21 Aug 2026): opening the calculator populates
    // the default monthly/deposit/term into the observed nodes ~800ms later,
    // which made a plain open look like a calculation. The observer now only
    // fires when a real input/change/click happened in the last 30s.
    var realInteractionTs = 0;
    function attachResultObserver() {
      if (!observer) observer = new MutationObserver(function () {
        if (now() - realInteractionTs < 30000) fire();
      });
      var targets = [];
      var idEl = document.getElementById('monthlyPaymentResult');
      if (idEl) targets.push(idEl);
      // Modal monthly displays.
      Array.prototype.push.apply(targets, document.querySelectorAll('[data-oasis-monthly-display]'));
      // Hidden form input that gets repopulated by the calc script.
      Array.prototype.push.apply(targets, document.querySelectorAll('[data-oasis-property="monthly-payment"]'));
      targets.forEach(function (el) {
        if (!el || el.__oasisObserved) return;
        el.__oasisObserved = true;
        observer.observe(el, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['value'] });
      });
    }
    attachResultObserver();
    // Re-check periodically for late-mounted calculators (e.g. modal opens).
    var attempts = 0;
    var poll = setInterval(function () {
      attachResultObserver();
      attempts++;
      if (attempts > 40) clearInterval(poll); // ~20s
    }, 500);

    // Started Reservation — delegated submit handler.
    // Catches ANY cart-add form on the site (calculator's Reserve form,
    // product page Reserve, collection card Reserve, drawer Reserve).
    // The lot_handle is resolved from the form's hidden inputs, the
    // closest [data-product-handle] element, or the page context.
    function fireStartedReservation(handle, source) {
      var monthly = readById('monthlyPaymentResult');
      api.track('Started Reservation', {
        __unique_id: session.id + ':' + (handle || 'unknown'),
        lot_handle: handle || (ctx.product && ctx.product.handle) || undefined,
        source: source || undefined,
        monthly_payment: isFinite(monthly) ? Math.round(monthly * 100) / 100 : undefined,
        deposit_pct: readById('depositPercentage'),
        years: readById('years')
      });
    }

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.tagName || form.tagName !== 'FORM') return;
      var action = form.getAttribute('action') || '';
      if (!/cart\/add/.test(action)) return;

      // Try to resolve the lot handle from a few likely places.
      var handle = '';
      var handleAttr = form.querySelector('[name="properties[handle]"], [data-product-handle]');
      if (handleAttr) handle = handleAttr.value || handleAttr.getAttribute('data-product-handle') || '';
      if (!handle && ctx.product && ctx.product.handle) handle = ctx.product.handle;

      // Source tag: which UI surface did the submit come from?
      var calcEl = form.closest('#calculator, [id^="calculator"], .land-calculator, .payment-calc, .payment-calculator');
      var cardEl = form.closest('[data-product-handle], .product-card, .oasis-property-card, .product-card-land, .product-card-revamp');
      var drawerEl = form.closest('.product-drawer, [id^="product-drawer-"], .slide-over-panel');
      var source = calcEl ? 'calculator'
                 : drawerEl ? 'drawer'
                 : cardEl ? 'card'
                 : (ctx.pageType === 'product' ? 'product-page' : 'unknown');

      fireStartedReservation(handle, source);
    }, true);

    // Also catch direct Reserve button clicks where the button is NOT
    // inside a form (some collection cards open a quick-add or link).
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest(
        '[data-oasis-reserve], button.reserve, a.reserve, .reserve-cta, [data-reserve]'
      );
      if (!btn) return;
      // If the click is inside a form that will also submit, the submit
      // handler above will fire — avoid duplicate firing.
      if (btn.closest('form[action*="cart/add"]')) return;
      var handle = btn.getAttribute('data-product-handle') ||
                   btn.getAttribute('data-handle') ||
                   (btn.closest('[data-product-handle]') && btn.closest('[data-product-handle]').getAttribute('data-product-handle')) ||
                   '';
      fireStartedReservation(handle, 'button-click');
    }, true);
  }

  // ----- Storefront form submitted -----
  // Fires when any user-facing form is submitted on the storefront,
  // EXCLUDING cart-add forms (those become Started Reservation), Klaviyo
  // signup forms (Klaviyo tracks those itself), and Shopify account/auth
  // forms. The intent is to capture lead-gen-style submissions: contact
  // forms, brochure-request forms, quiz lead-capture forms, etc.
  function wireFormSubmitted() {
    var SKIP_ACTION_PATTERNS = [
      /\/cart\/add/i,
      /\/cart\/update/i,
      /\/cart\/change/i,
      /\/cart\.js/i,
      /\/checkout/i,
      /\/account\/login/i,
      /\/account\/register/i,
      /\/account\/recover/i,
      /\/account\/activate/i,
      /\/localization/i,
      /klaviyo\.com/i,
      /\/search/i
    ];
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      if (form.getAttribute('data-oasis-track') === 'off') return;
      var action = (form.getAttribute('action') || '').replace(/^https?:\/\/[^/]+/i, '').trim();
      for (var i = 0; i < SKIP_ACTION_PATTERNS.length; i++) {
        if (SKIP_ACTION_PATTERNS[i].test(action)) return;
      }
      // Only count if the form has at least one email or phone input
      // (otherwise it's likely a filter, search, or non-lead form).
      var hasContact = form.querySelector('input[type="email"], input[name*="email" i], input[type="tel"], input[name*="phone" i]');
      if (!hasContact) return;

      var formName = form.getAttribute('data-oasis-form-name') ||
                     form.getAttribute('name') ||
                     form.getAttribute('id') ||
                     'unknown';
      var emailInput = form.querySelector('input[type="email"], input[name*="email" i]');
      var emailVal = emailInput ? (emailInput.value || '').trim() : '';

      // If the form carries an email, also identify the profile so the
      // event attaches and future browsing links to this identity.
      if (emailVal && /@/.test(emailVal)) {
        api.identify({ $email: emailVal });
      }

      api.track('Submitted Form', {
        __unique_id: session.id + ':form:' + formName + ':' + Math.floor(now() / 60000),
        form_name: formName,
        action: action,
        page_type: ctx.pageType,
        path: location.pathname
      });
    }, true);
  }

  // ----- Opened Payment Calculator -----
  // Fires when the user clicks the "Click here to customise your
  // interest-free payment plan" banner (or any element wired as a
  // calculator opener). This is distinct from `Calculated Payment`
  // (which fires only when they actually change an input) — opening
  // the widget is itself a high-intent micro-signal.
  function wireOpenedCalculator() {
    document.addEventListener('click', function (e) {
      var trigger = e.target && e.target.closest && e.target.closest(
        '.price-interactive-container, .js-reserve-open, [aria-label="Open payment calculator"], [aria-label="Open Payment Calculator"], [data-oasis-open-calculator], [onclick*="openPaymentCalculator"]'
      );
      if (!trigger) return;
      var handle = (ctx.product && ctx.product.handle) || '';
      var bucket = Math.floor(now() / 60000);
      api.track('Opened Payment Calculator', {
        __unique_id: (handle || 'unknown') + ':opened-calc:' + bucket,
        lot_handle: handle || undefined,
        lot_price: ctx.product && ctx.product.price,
        page_type: ctx.pageType,
        path: location.pathname
      });
    }, { capture: true, passive: true });
  }

  // ----- Viewed Payment Plan Page -----
  // Fires once per session when the visitor lands on a dedicated payment
  // plan page (matches the handover's "Payment plan page = 10 points"
  // page-visit signal). Detected via URL pattern OR presence of a
  // calculator widget on a non-product page.
  function wireViewedPaymentPlan() {
    var urlMatch = /\/pages\/(payment-plan|payment-plans|aiseo-payment-plans|how-it-works)/i.test(location.pathname);
    var hasCalc = !!document.getElementById('calculator') ||
                  !!document.getElementById('monthlyPaymentResult') ||
                  !!document.querySelector('.land-calculator, .payment-calc');
    if (!urlMatch && !(hasCalc && ctx.pageType !== 'product')) return;
    api.track('Viewed Payment Plan Page', {
      __unique_id: location.pathname + ':' + session.id,
      page_type: ctx.pageType,
      has_calculator: hasCalc
    });
  }

  // ----- Scroll depth -----
  function wireScroll() {
    if (document.body && document.body.classList.contains('shopify-design-mode')) return;
    var docEl = document.documentElement;
    var firedKey = 'scroll:' + location.pathname + ':';
    var thresholds = SCROLL_THRESHOLDS.slice();
    var onScroll = throttle(function () {
      if (!thresholds.length) return;
      var scrollTop = window.pageYOffset || docEl.scrollTop || 0;
      var viewport = window.innerHeight || docEl.clientHeight || 0;
      var height = (docEl.scrollHeight || document.body.scrollHeight || 0) - viewport;
      if (height <= 0) return;
      var pct = Math.min(100, Math.max(0, Math.round((scrollTop / height) * 100)));
      while (thresholds.length && pct >= thresholds[0]) {
        var depth = thresholds.shift();
        if (hasFired(firedKey + depth)) continue;
        markFired(firedKey + depth);
        api.track('Scrolled Page', {
          __unique_id: location.pathname + ':' + depth + ':' + session.id,
          depth: depth,
          title: (document.title || '').slice(0, 200)
        });
      }
    }, 250);
    window.addEventListener('scroll', onScroll, { passive: true });
    // Run once on mount in case the page is already deep (anchor links).
    setTimeout(onScroll, 500);
  }

  // ----- Article reading -----
  function wireArticleRead() {
    if (ctx.pageType !== 'article' || !ctx.article || !ctx.article.handle) return;
    var docEl = document.documentElement;
    var arrived = now();
    var deepEnough = false;
    var firedKey = 'article_read:' + ctx.article.handle;

    function checkAndFire() {
      if (hasFired(firedKey)) return true;
      if (!deepEnough) return false;
      var elapsed = (now() - arrived) / 1000;
      if (elapsed < ARTICLE_READ_MIN_SECONDS) return false;
      markFired(firedKey);
      api.track('Read Article', {
        __unique_id: ctx.article.handle + ':' + session.id,
        article_handle: ctx.article.handle,
        article_title: ctx.article.title || undefined,
        blog_handle: ctx.article.blog_handle || undefined,
        time_on_page_seconds: Math.round(elapsed)
      });
      return true;
    }

    var onScroll = throttle(function () {
      var scrollTop = window.pageYOffset || docEl.scrollTop || 0;
      var viewport = window.innerHeight || docEl.clientHeight || 0;
      var height = (docEl.scrollHeight || document.body.scrollHeight || 0) - viewport;
      if (height <= 0) return;
      var pct = (scrollTop / height) * 100;
      if (pct >= ARTICLE_READ_MIN_SCROLL) {
        deepEnough = true;
        if (checkAndFire()) {
          window.removeEventListener('scroll', onScroll);
        }
      }
    }, 500);
    window.addEventListener('scroll', onScroll, { passive: true });

    // Also check periodically for the time gate (in case the user scrolls fast).
    var interval = setInterval(function () {
      if (checkAndFire()) clearInterval(interval);
    }, 5000);
    window.addEventListener('beforeunload', function () { clearInterval(interval); });
  }

  // ----- Brochure / PDF downloads -----
  function wireBrochures() {
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      var href = a.getAttribute('href') || '';
      var explicit = a.hasAttribute('data-oasis-brochure');
      var isPdf = /\.pdf(\?|#|$)/i.test(href);
      if (!explicit && !isPdf) return;
      var label = a.getAttribute('data-oasis-brochure') || a.textContent.trim().slice(0, 80) || a.getAttribute('aria-label') || '';
      api.track('Downloaded Brochure', {
        __unique_id: href + ':' + session.id,
        brochure_url: href,
        brochure_label: label
      });
      api.setProperty('Last Brochure Downloaded At', nowIso());
    }, { capture: true, passive: true });
  }

  // ----- YouTube video engagement -----
  // Strategy: find every YouTube embed iframe on the page (whether or not
  // it has enablejsapi=1 in the src). If it lacks the flag, rewrite the
  // src to add it BEFORE attaching the YT.Player. Rewriting forces the
  // iframe to reload, but it happens at boot before the user has
  // interacted, so the cost is invisible. Also re-scan periodically so
  // iframes added by carousels / lightboxes / lazy sections are caught.
  // Oasis embeds use the privacy-enhanced host (youtube-nocookie.com), which
  // the original youtube.com-only matcher never saw. 27 Aug 2026: every video
  // on the 17 new /pages/watch-*, video-library and reviews pages was
  // untracked for this reason. Both hosts are matched from here on.
  var YT_EMBED_SELECTOR =
    'iframe[src*="youtube.com/embed"], iframe[data-src*="youtube.com/embed"], ' +
    'iframe[src*="youtube-nocookie.com/embed"], iframe[data-src*="youtube-nocookie.com/embed"]';
  var YT_EMBED_RE = /youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]+)/;
  function isYouTubeEmbed(url) { return !!url && YT_EMBED_RE.test(url); }

  function ensureJsApiInUrl(url) {
    if (!url) return url;
    if (/[?&]enablejsapi=1/.test(url)) return url;
    var add = 'enablejsapi=1';
    try {
      if (location.origin && !/[?&]origin=/.test(url)) {
        add += '&origin=' + encodeURIComponent(location.origin);
      }
    } catch (e) {}
    return url + (url.indexOf('?') > -1 ? '&' : '?') + add;
  }

  function upgradeIframe(iframe) {
    if (iframe.__oasisUpgraded) return false;
    iframe.__oasisUpgraded = true;
    var srcAttr = iframe.getAttribute('src') || '';
    var dataSrc = iframe.getAttribute('data-src') || '';
    if (srcAttr && isYouTubeEmbed(srcAttr) && !/[?&]enablejsapi=1/.test(srcAttr)) {
      iframe.setAttribute('src', ensureJsApiInUrl(srcAttr));
      return true; // src changed = iframe will reload
    }
    if (dataSrc && isYouTubeEmbed(dataSrc) && !/[?&]enablejsapi=1/.test(dataSrc)) {
      iframe.setAttribute('data-src', ensureJsApiInUrl(dataSrc));
    }
    return false;
  }

  // Lazy-load the YouTube iframe API only when we have at least one embed.
  function loadYouTubeApi(cb) {
    if (window.YT && window.YT.Player) return cb();
    window.onYouTubeIframeAPIReady = (function (prev) {
      return function () {
        if (prev) try { prev(); } catch (e) {}
        cb();
      };
    })(window.onYouTubeIframeAPIReady);
    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) return;
    var tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.async = true;
    document.head.appendChild(tag);
  }

  // ---------------------------------------------------------------------
  // Passive YouTube tracking (rewritten 27 Aug 2026)
  //
  // We used to attach our own YT.Player to each embed. That breaks wherever
  // something else already owns the player: the /pages/watch-* pages carry
  // their own inline player script, and GTM's YouTube trigger inspects the
  // same iframes. A second YT.Player against an owned iframe never gets its
  // onReady, so the handshake silently timed out and nothing was tracked.
  //
  // Instead we listen. Any embed with enablejsapi=1 broadcasts `infoDelivery`
  // messages (playerState, currentTime, duration) to the parent window, and
  // every listener in the page receives them. So we register interest, then
  // read the stream. This cannot conflict with another owner, works on both
  // youtube.com and youtube-nocookie.com, and needs no YouTube API script.
  // ---------------------------------------------------------------------
  function ytLabelFor(iframe) {
    try {
      var t = (iframe.getAttribute('title') || '').trim();
      if (t && !/^youtube/i.test(t)) return t.slice(0, 120);
      var fig = iframe.closest && iframe.closest('figure, .video, .video-wrap, section, article');
      var h = fig && fig.querySelector('h1, h2, h3');
      if (h && h.textContent.trim()) return h.textContent.trim().slice(0, 120);
    } catch (e) {}
    return (document.title || '').split('|')[0].trim().slice(0, 120);
  }

  // weUpgraded === true means the embed arrived WITHOUT enablejsapi and we
  // added it, so no other script had set this player up: we own it and can
  // build immediately. false means it already carried the flag, which means
  // something else wired it (the watch-* pages' inline script) and we should
  // wait for their player to register and hook that instead.
  function attachYouTubePlayer(iframe, weUpgraded) {
    if (iframe.__oasisPlayerAttached) return;
    var src = iframe.getAttribute('src') || iframe.getAttribute('data-src') || '';
    var m = src.match(YT_EMBED_RE);
    if (!m) return;
    var videoId = m[1];
    // If the iframe already carries an id we did not set, something else on
    // the page manages this embed (the watch-* pages' own inline script does
    // exactly this). Remember that, because it decides whether we may build
    // our own player or must wait for theirs to register.
    var ownerLikely = (weUpgraded === false);
    if (!iframe.id) iframe.id = 'oasis-yt-' + videoId + '-' + Math.random().toString(36).slice(2, 7);
    iframe.__oasisPlayerAttached = true;

    var label = ytLabelFor(iframe);
    var firedThresholds = {};
    var checkInterval = null;
    // Deep-engagement state. The existing "Video Deep Engagement" score rule
    // only ever listened to HTML5 <video>, and every Oasis video is a YouTube
    // embed, so it had never fired. Replaying a video, or unmuting one, is a
    // far stronger signal than simply pressing play.
    var deep = { ended: false, wasMuted: null, fired: {} };

    function fireDeep(kind) {
      if (deep.fired[kind]) return;
      deep.fired[kind] = 1;
      api.track('Video Deep Engagement', {
        __unique_id: videoId + ':' + kind + ':' + session.id,
        video_id: videoId,
        video_title: label || undefined,
        action: kind,
        page_type: ctx.pageType,
        path: location.pathname
      });
    }

    function fireProgress(player) {
      try {
        var dur = player.getDuration();
        var cur = player.getCurrentTime();
        if (!dur) return;
        var pct = Math.round((cur / dur) * 100);
        VIDEO_THRESHOLDS.forEach(function (t) {
          if (t < 100 && pct >= t && !firedThresholds[t]) {
            firedThresholds[t] = 1;
            api.track('Watched Lot Video', {
              __unique_id: videoId + ':' + t + ':' + session.id,
              video_id: videoId,
              video_title: label || undefined,
              depth: t,
              seconds_watched: Math.round(cur),
              duration_seconds: Math.round(dur),
              page_type: ctx.pageType,
              path: location.pathname
            });
          }
        });
      } catch (err) {}
    }

    function onState(e) {
      var player = e.target;
      if (e.data === window.YT.PlayerState.PLAYING) {
        if (deep.ended) fireDeep('replayed');
        var playedKey = 'video_played:' + videoId;
        if (!hasFired(playedKey)) {
          markFired(playedKey);
          var dur0 = 0;
          try { dur0 = player.getDuration() || 0; } catch (err) {}
          api.track('Played Lot Video', {
            __unique_id: videoId + ':' + session.id,
            video_id: videoId,
            video_title: label || undefined,
            duration_seconds: dur0 ? Math.round(dur0) : undefined,
            page_type: ctx.pageType,
            path: location.pathname
          });
        }
        if (!checkInterval) {
          checkInterval = setInterval(function () {
            fireProgress(player);
            // Unmuting is a deliberate act: they want to hear it.
            try {
              if (typeof player.isMuted === 'function') {
                var m = player.isMuted();
                if (deep.wasMuted === true && m === false) fireDeep('unmuted');
                deep.wasMuted = m;
              }
            } catch (err) {}
          }, 1000);
        }
      } else if (e.data === window.YT.PlayerState.ENDED) {
        deep.ended = true;
        if (checkInterval) { clearInterval(checkInterval); checkInterval = null; }
        if (!firedThresholds[100]) {
          firedThresholds[100] = 1;
          var dur1 = 0;
          try { dur1 = player.getDuration() || 0; } catch (err) {}
          api.track('Watched Lot Video', {
            __unique_id: videoId + ':100:' + session.id,
            video_id: videoId,
            video_title: label || undefined,
            depth: 100,
            duration_seconds: dur1 ? Math.round(dur1) : undefined,
            page_type: ctx.pageType,
            path: location.pathname
          });
        }
      }
    }

    // KEY (27 Aug 2026): do NOT blindly construct a player. The
    // /pages/watch-* pages ship their own inline YT.Player, and GTM's
    // YouTube trigger inspects the same iframes. A second YT.Player against
    // an already-owned iframe never receives onReady, so the old code
    // silently tracked nothing on every video page. Reuse the existing
    // player when there is one; only build our own when the embed is
    // genuinely unowned (lot pages, blog embeds).
    // Hook the owner's player if there is one; only build our own when the
    // embed is genuinely unowned. Retries a few times first, because an
    // owner's player may still be registering when we arrive - constructing
    // a second player against an owned iframe is precisely the bug that left
    // every video page untracked before 27 Aug 2026.
    var tries = 0;
    (function hookOrBuild() {
      var existing = null;
      try { existing = window.YT && window.YT.get ? window.YT.get(iframe.id) : null; } catch (err) {}

      if (existing && typeof existing.addEventListener === 'function') {
        try {
          existing.addEventListener('onStateChange', onState);
          // The owner may already be mid-play by the time we arrive.
          if (typeof existing.getPlayerState === 'function' &&
              existing.getPlayerState() === window.YT.PlayerState.PLAYING) {
            onState({ target: existing, data: window.YT.PlayerState.PLAYING });
          }
        } catch (err) { log('yt hook failed', err); }
        return;
      }

      // Another script owns this embed but has not registered its player
      // yet - wait for it rather than colliding. Bounded so a page that
      // merely ships enablejsapi in the markup still gets tracked.
      if (ownerLikely && tries < 5) {
        tries++;
        setTimeout(hookOrBuild, 600);
        return;
      }

      try {
        new window.YT.Player(iframe.id, { events: { onStateChange: onState } });
      } catch (err) { log('yt attach failed', err); }
    })();
  }

  function processVideoIframe(iframe, alreadyUpgraded) {
    // The DOM observer may have upgraded this iframe already and passed the
    // result through; upgradeIframe() is one-shot, so calling it twice would
    // wrongly report "no change" and make us treat our own embed as owned.
    var changed = (alreadyUpgraded === undefined) ? upgradeIframe(iframe) : alreadyUpgraded;
    loadYouTubeApi(function () {
      // If we rewrote the src the iframe is reloading, so give it a beat.
      setTimeout(function () { attachYouTubePlayer(iframe, changed); }, changed ? 700 : 150);
    });
  }

  function wireYouTube() {
    function findIframes() {
      return Array.prototype.slice.call(document.querySelectorAll(
        YT_EMBED_SELECTOR
      ));
    }

    findIframes().forEach(processVideoIframe);
    observeForVideos(processVideoIframe);
  }

  // Watch the DOM for new YouTube embed iframes and wire them as they
  // appear. Used both when the page boots empty (no videos yet) and
  // alongside the initial pass so dynamic embeds are caught.
  function observeForVideos(attachFn) {
    if (window.__oasisVideoObserverAttached) return;
    window.__oasisVideoObserverAttached = true;

    function scanNode(node) {
      if (!node || node.nodeType !== 1) return;
      var candidates = [];
      if (node.tagName === 'IFRAME') candidates.push(node);
      else if (node.querySelectorAll) {
        Array.prototype.push.apply(candidates,
          node.querySelectorAll(YT_EMBED_SELECTOR));
      }
      candidates.forEach(function (iframe) {
        var changed = upgradeIframe(iframe);
        if (attachFn) {
          // Give the iframe a tick to settle if we just rewrote its src.
          setTimeout(function () { attachFn(iframe, changed); }, changed ? 400 : 0);
        }
      });
    }

    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (!m.addedNodes) return;
        Array.prototype.forEach.call(m.addedNodes, scanNode);
      });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  // ----- Callback trigger -----
  function wireCallback() {
    document.addEventListener('click', function (e) {
      var trigger = e.target && e.target.closest && e.target.closest('[data-open-modal]');
      if (!trigger) return;
      api.track('Opened Callback', {
        __unique_id: session.id + ':opened-callback',
        cta_location: trigger.getAttribute('data-section') || ctx.pageType || location.pathname,
        cta_label: (trigger.textContent || '').trim().slice(0, 80)
      });
    }, { capture: true, passive: true });
  }

  // ----- Knowledge Hub tab views -----
  function wireKnowledgeHub() {
    if (!/\/pages\/knowledge-hub/.test(location.pathname)) return;
    var KNOWN = { '#faqs': 'faqs', '#land': 'land', '#brochures': 'land', '#videos': 'videos', '#blogs': 'blogs' };
    function fire() {
      var tab = KNOWN[location.hash];
      if (!tab) return;
      var key = 'kh_tab:' + tab;
      if (hasFired(key)) return;
      markFired(key);
      api.track('Viewed Knowledge Hub Tab', {
        __unique_id: tab + ':' + session.id,
        tab: tab
      });
    }
    window.addEventListener('hashchange', fire);
    fire();
  }

  // ----- Viewed Page / Viewed Collection -----
  // Generic page-view tracking. Fires once per (path × session). Skips pages
  // where a more specific event already fires (lot, article, knowledge hub,
  // payment plan, quiz). For /collections/* URLs, fires as `Viewed Collection`
  // with the handle as a property — replaces Klaviyo's Shopify auto-detect
  // which doesn't fire reliably on Oasis' custom collection layout.
  //
  // Calls api.track directly (no api.ready wrapper) — api.track itself
  // buffers events when consent/klaviyo aren't ready and drains them on
  // ready, matching every other wire in this file.
  function wireViewedPage() {
    var pageType = ctx.pageType || (document.body && document.body.dataset && document.body.dataset.pageType) || '';
    var path = location.pathname;

    // Skip pages where a more specific event already fires.
    var skipTypes = ['product', 'article'];
    if (skipTypes.indexOf(pageType) > -1) return;
    if (/\/pages\/knowledge-hub/.test(path)) return;
    if (/\/pages\/payment-plan/.test(path) || /\/pages\/finance/.test(path)) return;
    if (/\/pages\/quiz/.test(path) || /\/pages\/lot-finder/.test(path)) return;

    // /collections/* → Viewed Collection (replaces broken Klaviyo Shopify auto-fire)
    // Dedup is handled by api.track via __unique_id; no external hasFired needed.
    //
    // 18 Sep 2026: Shopify serves localised markets from a path prefix, so the
    // same page is /collections/all to one visitor and /en-us/collections/all
    // to another. The test here was indexOf('/collections/') === 0, which is 6
    // on a prefixed URL, not 0. Every prefixed collection view therefore fell
    // through to the Viewed Page branch below, with no collection_handle on it:
    // 92 of 724 collection views, 12.7%, invisible to anything keyed on
    // Viewed Collection. Strip the prefix before testing. The full path is
    // still what gets reported.
    var barePath = path.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/)/i, '');
    if (barePath.indexOf('/collections/') === 0) {
      var handle = barePath.replace(/^\/collections\//, '').split('/')[0] || 'all';
      api.track('Viewed Collection', {
        __unique_id: 'collection:' + handle + ':' + session.id,
        collection_handle: handle,
        path: path,
        page_type: 'collection',
        title: (document.title || '').slice(0, 200)
      });
      return;
    }

    // Everything else → Viewed Page
    api.track('Viewed Page', {
      __unique_id: 'page:' + path + ':' + session.id,
      path: path,
      page_type: pageType || 'unknown',
      title: (document.title || '').slice(0, 200)
    });
  }

  // ---------------------------------------------------------------------
  // Boot — wire everything on DOM ready (or now if past it)
  // ---------------------------------------------------------------------
  function boot() {
    try { wireViewedLot(); } catch (e) { log('viewedlot boot fail', e); }
    // wireLotDrawer removed — product drawer UI deprecated.
    try { wireQuiz(); } catch (e) { log('quiz boot fail', e); }
    try { wireCalculator(); } catch (e) { log('calc boot fail', e); }
    try { wireOpenedCalculator(); } catch (e) { log('openedcalc boot fail', e); }
    try { wireFormSubmitted(); } catch (e) { log('form boot fail', e); }
    try { wireViewedPaymentPlan(); } catch (e) { log('payplan boot fail', e); }
    try { wireScroll(); } catch (e) { log('scroll boot fail', e); }
    try { wireArticleRead(); } catch (e) { log('article boot fail', e); }
    try { wireBrochures(); } catch (e) { log('brochure boot fail', e); }
    try { wireYouTube(); } catch (e) { log('youtube boot fail', e); }
    try { wireCallback(); } catch (e) { log('callback boot fail', e); }
    try { wireKnowledgeHub(); } catch (e) { log('kh boot fail', e); }
    try { wireViewedPage(); } catch (e) { log('viewedpage boot fail', e); }
    log('booted in pageType=', ctx.pageType);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else if ('requestIdleCallback' in window) {
    requestIdleCallback(boot, { timeout: 1500 });
  } else {
    setTimeout(boot, 300);
  }

  // iOS Safari (and Chrome on Android) restore back/forward navigations from
  // BFCache without re-firing DOMContentLoaded, so the initial boot pass
  // misses them. api.track dedup is session-scoped via __unique_id, so
  // re-firing the page-view wires here is idempotent.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    log('pageshow restored from BFCache — re-firing page-view events');
    try { wireViewedLot(); } catch (err) {}
    try { wireViewedPage(); } catch (err) {}
    try { wireViewedPaymentPlan(); } catch (err) {}
  });
})();
